import PlusIcon from "@redotech/redo-icons/plus.svg";
import type { RedoListItem } from "../../arbiter-components/list/redo-list";
import type { ArrayStringTableFilter } from "../advanced-filter";

/** Typed search string to add as a filter value, if it is not already listed. */
export function customFilterValueToAdd({
  allowCustomValues,
  search,
  existingValues,
}: {
  allowCustomValues: boolean | undefined;
  search: string;
  existingValues: readonly string[];
}): string | undefined {
  if (!allowCustomValues) {
    return undefined;
  }
  const trimmed = search.trim();
  if (!trimmed) {
    return undefined;
  }
  const needle = trimmed.toLowerCase();
  if (existingValues.some((value) => value.trim().toLowerCase() === needle)) {
    return undefined;
  }
  return trimmed;
}

export function customFilterValueListItem(
  customValue: string,
): RedoListItem<string> {
  return {
    id: customValue,
    value: customValue,
    type: "text",
    text: customValue,
    leadingItem: { type: "icon", Icon: PlusIcon },
  };
}

export function getArrayStringFilterExistingValues(
  optionsWithSelected: readonly RedoListItem<string>[],
  selectedItems: readonly string[],
): string[] {
  return [
    ...optionsWithSelected.map((item) => String(item.id)),
    ...selectedItems,
  ];
}

export function arrayStringFilterItemsWithCustomValue({
  filter,
  searchText,
  searchedMatches,
  optionsWithSelected,
  selectedItems,
}: {
  filter: Pick<ArrayStringTableFilter, "allowCustomValues">;
  searchText: string;
  searchedMatches: RedoListItem<string>[];
  optionsWithSelected: RedoListItem<string>[];
  selectedItems: string[];
}): RedoListItem<string>[] {
  const customValue = customFilterValueToAdd({
    allowCustomValues: filter.allowCustomValues,
    search: searchText,
    existingValues: getArrayStringFilterExistingValues(
      optionsWithSelected,
      selectedItems,
    ),
  });
  return listItemsWithOptionalCustomValue(
    searchedMatches,
    customValue ? customFilterValueListItem(customValue) : undefined,
  );
}

export function createArrayStringCustomValueSearchSubmitted({
  filter,
  setFilter,
  optionsWithSelected,
  selectedItems,
  setSearchText,
}: {
  filter: ArrayStringTableFilter;
  setFilter: (filter: ArrayStringTableFilter) => void;
  optionsWithSelected: RedoListItem<string>[];
  selectedItems: string[];
  setSearchText: (text: string) => void;
}): ((search: string) => void) | undefined {
  if (!filter.allowCustomValues) {
    return undefined;
  }
  return (search) => {
    const next = customFilterValueToAdd({
      allowCustomValues: true,
      search,
      existingValues: getArrayStringFilterExistingValues(
        optionsWithSelected,
        selectedItems,
      ),
    });
    if (!next) {
      return;
    }
    setFilter({
      ...filter,
      data: {
        ...filter.data,
        value: appendCustomFilterValue(selectedItems, next),
      },
    });
    setSearchText("");
  };
}

export function mergeSelectedListItems({
  options,
  selected,
}: {
  options: RedoListItem<string>[];
  selected: RedoListItem<string>[];
}): RedoListItem<string>[] {
  if (selected.length === 0) {
    return options;
  }
  const seen = new Set(options.map((item) => item.id));
  const extras = selected.filter((item) => !seen.has(item.id));
  if (extras.length === 0) {
    return options;
  }
  return [...options, ...extras];
}

export function listItemsWithOptionalCustomValue(
  items: RedoListItem<string>[],
  customItem: RedoListItem<string> | undefined,
): RedoListItem<string>[] {
  if (!customItem || items.some((item) => item.id === customItem.id)) {
    return items;
  }
  return [...items, customItem];
}

export function appendCustomFilterValue(
  currentValues: readonly string[],
  customValue: string,
): string[] {
  const needle = customValue.trim().toLowerCase();
  if (currentValues.some((value) => value.trim().toLowerCase() === needle)) {
    return [...currentValues];
  }
  return [...currentValues, customValue];
}
